import { activityFlatOfInfo } from '@components/pages/activity-log/services/activity-log-flat.util';
import type { IActivityFlat } from '@components/pages/activity-log/services/activity-log-flat.util';
import type { FlatInfo } from '@models/flat-info.model';
import { GuestInfo, GuestInfoDisplayMode } from '@models/guest-info.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from '@services/api/api.service';
import { BookingClientService } from '@services/api/booking/booking-client.service';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
// Services
import { ConfirmableLookaheadStorageService } from '@services/storage/confirmable-lookahead-storage.service';
import { GuestInfoDataSourceStorageService } from '@services/storage/guest-info-data-source-storage.service';
import { HardCodedConfigService } from '@services/util/hard-coded-config.service';
import { CheckinClipboardService } from '@services/util/checkin-clipboard.service';
import { LogService } from '@services/util/log.service';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SliderModule } from 'primeng/slider';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { debounceTime, distinctUntilChanged, ReplaySubject, Subscription, take } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
// Components
import { BuildingFilterComponent } from '../../service-pages/arrival-departure/components/building-filter/building-filter.component';
import { HostActivityLogService } from '../shared/host-activity-log.service';
import { bookingRefMetadata, checkinPasteMetadata } from '../shared/host-activity.metadata';
import { ArrivalsTableComponent } from './components/arrivals-table/arrivals-table.component';
import { CheckinSmsModalComponent } from './components/checkin-sms-modal/checkin-sms-modal.component';
import { CommunicationModalComponent } from './components/communication-modal/communication-modal.component';
import { ConfirmableTableComponent } from './components/confirmable-table/confirmable-table.component';
import { DeparturesTableComponent } from './components/departures-table/departures-table.component';
import { GuestsTableComponent } from './components/guests-table/guests-table.component';
import { TabLoadBadgeComponent } from './components/tab-load-badge/tab-load-badge.component';
// Models
import { ArrivalBookingRow, ConfirmableBookingRow, DepartureBookingRow, GuestBookingRow } from './models/booking-overview.model';
import { INITIAL_LIST_LOAD_STATUSES, ListLoadStatus, ListLoadStatuses, OverviewList, withListStatus } from './models/list-load-state';
import { BookingActionService } from './services/booking-action.service';
import { BookingEntryCodeService, EntryCodeIndex, EntryCodeRow } from './services/booking-entry-code.service';
import { BookingOverviewService } from './services/booking-overview.service';
import { BookingPresenceService, PresenceIndex, PresenceRow } from './services/booking-presence.service';
import { PresenceWarningService } from './services/presence-warning.service';

@Component({
  selector: 'app-booking-overview',
  standalone: true,
  imports: [
    CommonModule,
    TabViewModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    ToastModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    DropdownModule,
    SliderModule,
    BuildingFilterComponent,
    ConfirmableTableComponent,
    ArrivalsTableComponent,
    GuestsTableComponent,
    DeparturesTableComponent,
    TabLoadBadgeComponent,
    CommunicationModalComponent,
    CheckinSmsModalComponent
  ],
  providers: [BookingOverviewService, MessageService],
  template: `
    <div class="compact-card">
      <!-- Toast for notifications -->
      <p-toast></p-toast>

      <!-- Header -->
      <div class="card-header mb-6">
        <h1 class="text-2xl font-bold">{{ 'bookingOverview.pageTitle' | translate }}</h1>
        <p class="mt-1 text-base text-gray-600 dark:text-gray-400">{{ 'bookingOverview.pageDescription' | translate }}</p>
      </div>

      <!-- Filters -->
      <div class="mx-4 mb-4 flex flex-wrap items-center justify-between gap-4 sm:mx-0">
        <!-- Building filter -->
        <div class="flex-grow">
          <app-building-filter
            [selectedBuilding]="selectedBuilding"
            (buildingSelected)="onBuildingSelected($event)"
          ></app-building-filter>
        </div>

        <!-- Guest Info Data Source Dropdown -->
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ 'bookingOverview.guestInfoSource' | translate }}:
          </label>
          <p-dropdown
            [options]="displayModeOptions"
            [(ngModel)]="selectedDisplayMode"
            optionLabel="label"
            optionValue="value"
            (onChange)="onDisplayModeChange()"
            styleClass="w-40"
          >
            <ng-template pTemplate="selectedItem" let-option>
              {{ option.label | translate }}
            </ng-template>
            <ng-template pTemplate="item" let-option>
              {{ option.label | translate }}
            </ng-template>
          </p-dropdown>
        </div>

        <!-- Search field -->
        <div class="w-full sm:w-auto md:w-1/3">
          <p-inputGroup>
            <p-inputGroupAddon>
              <i class="pi pi-search"></i>
            </p-inputGroupAddon>
            <input
              type="text"
              pInputText
              [formControl]="searchControl"
              placeholder="{{ 'bookingOverview.searchBookings' | translate }}"
              class="w-full"
            />
            @if (searchControl.value) {
              <p-inputGroupAddon>
                <button
                  class="p-inputgroup-addon cursor-pointer"
                  (click)="clearSearch()"
                  [attr.aria-label]="'common.clearSearch' | translate"
                >
                  <i class="pi pi-times"></i>
                </button>
              </p-inputGroupAddon>
            }
          </p-inputGroup>
        </div>
      </div>

      <p-tabView [activeIndex]="activeTabIndex" (activeIndexChange)="onTabChange($event)">
        <p-tabPanel>
          <ng-template pTemplate="header">
            <div class="align-items-center flex gap-2">
              <span>{{ 'bookingOverview.confirmable' | translate }}</span>
              <app-tab-load-badge [status]="listStatus().confirmable" [count]="filteredConfirmableBookings.length" />
            </div>
          </ng-template>
          <ng-template pTemplate="content">
            <div class="mb-4 flex items-center gap-4 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
              <i class="pi pi-calendar text-gray-500"></i>
              <label class="whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" for="confirmableLookaheadSlider">
                {{ 'bookingOverview.confirmableLookaheadLabel' | translate }}:
              </label>
              <p-slider
                inputId="confirmableLookaheadSlider"
                [(ngModel)]="confirmableLookaheadDays"
                [min]="lookaheadMin"
                [max]="lookaheadMax"
                [step]="1"
                (onSlideEnd)="onLookaheadChange()"
                styleClass="w-48 max-w-full"
              ></p-slider>
              <span class="min-w-[5rem] text-sm font-semibold text-gray-900 dark:text-gray-100">
                {{ 'bookingOverview.confirmableLookaheadValue' | translate: { days: confirmableLookaheadDays } }}
              </span>
            </div>
            <app-confirmable-table
              [confirmableBookings]="filteredConfirmableBookings"
              [isFilterActive]="isFilterActive"
              [totalCount]="confirmableBookings.length"
              [displayMode]="selectedDisplayMode"
              [loadStatus]="listStatus().confirmable"
              (retry)="loadConfirmableBookings()"
              (guestInfoClicked)="onViewBookingClick($event)"
              (communicationClicked)="onCommunicationClicked($event)"
              (presenceClicked)="onPresenceClick($event)"
            ></app-confirmable-table>
          </ng-template>
        </p-tabPanel>

        <p-tabPanel>
          <ng-template pTemplate="header">
            <div class="align-items-center flex gap-2">
              <span>{{ 'bookingOverview.arrivals' | translate }}</span>
              <app-tab-load-badge [status]="listStatus().arrivals" [count]="filteredArrivals.length" />
            </div>
          </ng-template>
          <ng-template pTemplate="content">
            <app-arrivals-table
              [arrivals]="filteredArrivals"
              [isFilterActive]="isFilterActive"
              [totalCount]="arrivals.length"
              [displayMode]="selectedDisplayMode"
              [loadStatus]="listStatus().arrivals"
              (retry)="loadArrivals()"
              [hasCheckinInClipboard]="clipboardService.hasCheckinInClipboard()"
              (guestInfoClicked)="onViewBookingClick($event)"
              (copyCheckinLinkClicked)="onCopyCheckinLinkClicked($event)"
              (sendCheckinSmsClicked)="onSendCheckinSmsClicked($event)"
              (communicationClicked)="onCommunicationClicked($event)"
              (copyCheckin)="onCopyCheckin($event)"
              (pasteCheckin)="onPasteCheckin($event)"
              (presenceClicked)="onPresenceClick($event)"
            ></app-arrivals-table>
          </ng-template>
        </p-tabPanel>

        <p-tabPanel>
          <ng-template pTemplate="header">
            <div class="align-items-center flex gap-2">
              <span>{{ 'bookingOverview.guests' | translate }}</span>
              <app-tab-load-badge [status]="listStatus().guests" [count]="filteredGuests.length" />
            </div>
          </ng-template>
          <ng-template pTemplate="content">
            <app-guests-table
              [guests]="filteredGuests"
              [isFilterActive]="isFilterActive"
              [totalCount]="guests.length"
              [displayMode]="selectedDisplayMode"
              [loadStatus]="listStatus().guests"
              (retry)="loadGuests()"
              [hasCheckinInClipboard]="clipboardService.hasCheckinInClipboard()"
              (generateInvoice)="onGenerateInvoiceClick($event)"
              (guestInfoClicked)="onViewBookingClick($event)"
              (communicationClicked)="onCommunicationClicked($event)"
              (copyCheckin)="onCopyCheckin($event)"
              (pasteCheckin)="onPasteCheckin($event)"
              (presenceClicked)="onPresenceClick($event)"
            ></app-guests-table>
          </ng-template>
        </p-tabPanel>

        <p-tabPanel>
          <ng-template pTemplate="header">
            <div class="align-items-center flex gap-2">
              <span>{{ 'bookingOverview.departures' | translate }}</span>
              <app-tab-load-badge [status]="listStatus().departures" [count]="filteredDepartures.length" />
            </div>
          </ng-template>
          <ng-template pTemplate="content">
            <app-departures-table
              [departures]="filteredDepartures"
              [isFilterActive]="isFilterActive"
              [totalCount]="departures.length"
              [displayMode]="selectedDisplayMode"
              [loadStatus]="listStatus().departures"
              (retry)="loadDepartures()"
              [hasCheckinInClipboard]="clipboardService.hasCheckinInClipboard()"
              (checkOut)="onCheckOutClick($event)"
              (cancelCheckOut)="onCancelCheckOutClick($event)"
              (guestInfoClicked)="onViewBookingClick($event)"
              (communicationClicked)="onCommunicationClicked($event)"
              (copyCheckin)="onCopyCheckin($event)"
              (pasteCheckin)="onPasteCheckin($event)"
              (presenceClicked)="onPresenceClick($event)"
              (linkChain)="onChainLinkRequested($event)"
            ></app-departures-table>
          </ng-template>
        </p-tabPanel>
      </p-tabView>

      <!-- Communication Modal -->
      <app-communication-modal
        [visible]="showCommunicationModal()"
        [guestInfo]="selectedGuestForCommunication()"
        [booking]="selectedBookingForCommunication()"
        [entryCodes]="selectedEntryCodesForCommunication()"
        (closed)="onCommunicationModalClosed()"
      ></app-communication-modal>

      <!-- Check-in SMS Modal -->
      <app-checkin-sms-modal
        [visible]="showCheckinSmsModal()"
        [booking]="selectedBookingForCheckinSms()"
        (closed)="onCheckinSmsModalClosed()"
      ></app-checkin-sms-modal>

      <!-- Paste Checkin Modal -->
      <p-dialog
        [(visible)]="showPasteCheckinModal"
        [header]="'bookingOverview.pasteCheckin.title' | translate"
        [modal]="true"
        [style]="{ width: '500px' }"
        [draggable]="false"
        [resizable]="false"
        (onHide)="onPasteCheckinModalClosed()"
      >
        <div class="flex flex-col gap-4">
          <!-- Warning message -->
          <p class="text-sm text-gray-600 dark:text-gray-300">
            {{ 'bookingOverview.pasteCheckin.description' | translate }}
          </p>

          @if (clipboardService.clipboard() && selectedTargetBooking()) {
            <!-- Source Booking Info -->
            <div class="rounded border border-gray-200 bg-blue-50 p-3">
              <h4 class="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                <i class="pi pi-copy mr-1"></i>
                {{ 'bookingOverview.pasteCheckin.sourceBooking' | translate }}
              </h4>
              <div class="space-y-1 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">{{ 'bookingOverview.guest' | translate }}:</span>
                  <span class="font-medium">{{ clipboardService.clipboard()!.sourceBooking.guestName }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">{{ 'bookingOverview.reservationCode' | translate }}:</span>
                  <span class="font-medium">{{ clipboardService.clipboard()!.sourceBooking.reservationCode }}</span>
                </div>
                @if (clipboardService.clipboard()!.sourceBooking.startDate && clipboardService.clipboard()!.sourceBooking.endDate) {
                  <div class="flex justify-between">
                    <span class="text-gray-600 dark:text-gray-400">{{ 'bookingOverview.period' | translate }}:</span>
                    <span class="font-medium">
                      {{ clipboardService.clipboard()!.sourceBooking.startDate }} → {{ clipboardService.clipboard()!.sourceBooking.endDate }}
                    </span>
                  </div>
                }
              </div>
            </div>

            <!-- Arrow Icon -->
            <div class="flex justify-center">
              <i class="pi pi-arrow-down text-2xl text-primary"></i>
            </div>

            <!-- Target Booking Info -->
            <div class="rounded border border-gray-200 bg-green-50 p-3">
              <h4 class="mb-2 text-sm font-semibold text-gray-700">
                <i class="pi pi-clipboard mr-1"></i>
                {{ 'bookingOverview.pasteCheckin.targetBooking' | translate }}
              </h4>
              <div class="space-y-1 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-600">{{ 'bookingOverview.guest' | translate }}:</span>
                  <span class="font-medium">{{ selectedTargetBooking()!.guest.name }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">{{ 'bookingOverview.reservationCode' | translate }}:</span>
                  <span class="font-medium">#{{ selectedTargetBooking()!.id }}</span>
                </div>
                @if (hasBookingPeriod(selectedTargetBooking()!)) {
                  <div class="flex justify-between">
                    <span class="text-gray-600">{{ 'bookingOverview.period' | translate }}:</span>
                    <span class="font-medium">{{ getBookingPeriod(selectedTargetBooking()!) }}</span>
                  </div>
                }
              </div>
            </div>

            <!-- Warning -->
            <div class="flex items-start gap-2 rounded bg-yellow-50 p-3 text-sm text-yellow-800">
              <i class="pi pi-exclamation-triangle mt-0.5"></i>
              <span>{{ 'bookingOverview.pasteCheckin.warning' | translate }}</span>
            </div>
          }
        </div>

        <ng-template pTemplate="footer">
          <button
            pButton
            [label]="'common.cancel' | translate"
            icon="pi pi-times"
            class="p-button-text"
            [disabled]="pasteCheckinLoading()"
            (click)="onPasteCheckinModalClosed()"
          ></button>
          <button
            pButton
            [label]="'bookingOverview.pasteCheckin.confirm' | translate"
            icon="pi pi-check"
            [loading]="pasteCheckinLoading()"
            [disabled]="!clipboardService.clipboard() || !selectedTargetBooking()"
            (click)="confirmPasteCheckin()"
          ></button>
        </ng-template>
      </p-dialog>

      <!-- Presence Detail Modal -->
      <p-dialog
        [(visible)]="presenceModalVisible"
        [modal]="true"
        [closable]="true"
        [style]="{ width: '400px' }"
        [dismissableMask]="true"
        (onHide)="onPresenceModalClose()"
      >
        <ng-template pTemplate="header">
          @if (selectedPresenceBooking()) {
            <div class="text-base font-semibold">
              {{ 'presence.details' | translate }}
            </div>
          }
        </ng-template>

        @if (selectedPresenceBooking() && selectedPresenceBooking().presenceStatus) {
          <div class="space-y-4">
            <!-- Booking Info -->
            <div class="border-b pb-3">
              <div class="text-sm text-gray-600 dark:text-gray-400">{{ 'bookingOverview.guest' | translate }}</div>
              <div class="font-medium">{{ selectedPresenceBooking().booking.guest.name }}</div>
              @if (selectedPresenceBooking().booking.flatInfos.length > 0) {
                <div class="mt-2 text-sm text-gray-600 dark:text-gray-400">{{ 'bookingOverview.flat' | translate }}</div>
                @for (flat of selectedPresenceBooking().booking.flatInfos; track flat.id; let i = $index) {
                  <div class="text-sm">{{ flat.buildingName }} › {{ flat.flatNumber }}</div>
                }
              }
            </div>

            <!-- Status Banner -->
            <div
              class="rounded-lg p-3"
              [class.bg-primary-50]="selectedPresenceBooking().presenceStatus.isOccupied"
              [class.text-primary-800]="selectedPresenceBooking().presenceStatus.isOccupied"
              [class.bg-gray-100]="!selectedPresenceBooking().presenceStatus.isOccupied"
              [class.text-gray-700]="!selectedPresenceBooking().presenceStatus.isOccupied"
            >
              <div class="flex items-center gap-3">
                @if (selectedPresenceBooking().presenceStatus.isOccupied) {
                  <div class="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                    <i class="pi pi-check text-lg text-green-600"></i>
                  </div>
                } @else {
                  <div class="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200">
                    <i class="pi pi-minus text-lg text-gray-400"></i>
                  </div>
                }
                <span class="font-medium">
                  {{ selectedPresenceBooking().presenceStatus.isOccupied ? ('presence.occupied' | translate) : ('presence.empty' | translate) }}
                </span>
              </div>
            </div>

            <!-- Time Info -->
            <div class="space-y-2 text-sm">
              <div class="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <i class="pi pi-clock"></i>
                <span>
                  {{ getPresenceTimeSince(selectedPresenceBooking().presenceStatus.minutesSinceChange) }}
                </span>
              </div>
              <div class="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <i class="pi pi-calendar"></i>
                <span>
                  {{ 'presence.lastChange' | translate }}: {{ formatPresenceDateTime(selectedPresenceBooking().presenceStatus.lastChanged) }}
                </span>
              </div>
            </div>

            <!-- Notice -->
            <div class="rounded bg-gray-50 p-3 text-xs text-gray-500">
              {{ 'presence.noHistoricalData' | translate }}
            </div>
          </div>
        }

        <ng-template pTemplate="footer">
          <button
            pButton
            [label]="'common.close' | translate"
            icon="pi pi-times"
            class="p-button-text"
            (click)="onPresenceModalClose()"
          ></button>
        </ng-template>
      </p-dialog>
    </div>
  `
})
export class BookingOverviewComponent implements OnInit {
  confirmableBookings: ConfirmableBookingRow[] = [];
  arrivals: ArrivalBookingRow[] = [];
  guests: GuestBookingRow[] = [];
  departures: DepartureBookingRow[] = [];

  filteredConfirmableBookings: ConfirmableBookingRow[] = [];
  filteredArrivals: ArrivalBookingRow[] = [];
  filteredGuests: GuestBookingRow[] = [];
  filteredDepartures: DepartureBookingRow[] = [];

  private pendingRowUpdate: { id: number; email?: string; phone?: string } | null = null;

  selectedBuilding: string | null = null;
  searchControl: FormControl = new FormControl('');
  /** A 4 lista betöltési állapota (tölt / kész / hiba) — a fülek és a táblázatok ebből rajzolnak. */
  readonly listStatus = signal<ListLoadStatuses>(INITIAL_LIST_LOAD_STATUSES);
  private confirmableLoad?: Subscription;
  /** Lapbetöltésenként egyszer lekért jelenlét- és belépőkód-index (spec 6.3); a listák ebből töltik ki a soraikat. */
  private readonly presenceIndex = new ReplaySubject<PresenceIndex>(1);
  private readonly entryCodeIndex = new ReplaySubject<EntryCodeIndex | null>(1);
  activeTabIndex: number = 0;

  selectedDisplayMode: GuestInfoDisplayMode = GuestInfoDisplayMode.USER;
  displayModeOptions = [
    { label: 'bookingOverview.dataSource.user', value: GuestInfoDisplayMode.USER },
    { label: 'bookingOverview.dataSource.booking', value: GuestInfoDisplayMode.BOOKING }
  ];

  // Confirmable tab — lookahead window in days (host-configurable via slider).
  confirmableLookaheadDays = 7;
  readonly lookaheadMin = 1;
  readonly lookaheadMax = 14;

  // Track if filtering is active
  get isFilterActive(): boolean {
    return this.selectedBuilding !== null || !!this.searchControl.value?.trim();
  }

  // Action loading states for individual actions
  private actionLoadingStates = new Map<string, boolean>();

  // Communication modal state
  showCommunicationModal = signal<boolean>(false);
  selectedGuestForCommunication = signal<GuestInfo | null>(null);
  selectedBookingForCommunication = signal<IBookingModel | null>(null);
  selectedEntryCodesForCommunication = signal<string[]>([]);

  // Paste checkin modal state
  showPasteCheckinModal = signal<boolean>(false);
  pasteCheckinLoading = signal<boolean>(false);
  selectedTargetBooking = signal<ArrivalBookingRow | GuestBookingRow | DepartureBookingRow | null>(null);

  // Presence modal state
  presenceModalVisible = signal<boolean>(false);
  selectedPresenceBooking = signal<any>(null);

  // Check-in SMS modal state
  showCheckinSmsModal = signal<boolean>(false);
  selectedBookingForCheckinSms = signal<ArrivalBookingRow | null>(null);

  /**
   * Get loading state for a specific action
   * @param action Action identifier
   * @returns True if the action is loading
   */
  getActionLoading(action: string): boolean {
    return this.actionLoadingStates.get(action) || false;
  }

  // Using Angular 19 dependency injection pattern
  private readonly bookingService = inject(BookingOverviewService);
  private readonly bookingClientService = inject(BookingClientService);
  private readonly entryCodeService = inject(BookingEntryCodeService);
  private readonly presenceWarningService = inject(PresenceWarningService);
  private readonly presenceService = inject(BookingPresenceService);
  private readonly bookingActionService = inject(BookingActionService);
  private readonly apiService = inject(ApiService);
  private readonly dataSourceStorage = inject(GuestInfoDataSourceStorageService);
  private readonly lookaheadStorage = inject(ConfirmableLookaheadStorageService);
  private readonly config = inject(HardCodedConfigService);
  readonly clipboardService = inject(CheckinClipboardService);
  private log = inject(LogService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostActivityLog = inject(HostActivityLogService);

  ngOnInit(): void {
    this.hostActivityLog.startVisit();
    const incoming = history.state;
    if (incoming?.updatedBookingId !== undefined) {
      this.pendingRowUpdate = {
        id: incoming.updatedBookingId,
        email: incoming.updatedFields?.email,
        phone: incoming.updatedFields?.phone
      };
    }

    const stored = this.dataSourceStorage.getDataSource();
    this.selectedDisplayMode = stored === 'booking' ? GuestInfoDisplayMode.BOOKING : GuestInfoDisplayMode.USER;

    this.confirmableLookaheadDays = this.lookaheadStorage.getDays();

    this.restoreStateFromUrl();

    this.loadIndexes();
    this.loadConfirmableBookings();
    this.loadArrivals();
    this.loadGuests();
    this.loadDepartures();

    // Setup search functionality with debounce
    this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateUrlParams();
      this.applyFilters();
    });
  }

  private setListStatus(list: OverviewList, status: ListLoadStatus): void {
    this.listStatus.update((statuses) => withListStatus(statuses, list, status));
  }

  private restoreStateFromUrl(): void {
    const params = this.route.snapshot.queryParams;
    if (params['tab'] !== undefined) {
      const tabIndex = parseInt(params['tab'], 10);
      if (!Number.isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 3) {
        this.activeTabIndex = tabIndex;
      }
    }
    if (params['building']) {
      this.selectedBuilding = params['building'];
    }
    if (params['search']) {
      this.searchControl.setValue(params['search'], { emitEvent: false });
    }
  }

  private updateUrlParams(): void {
    const queryParams: Record<string, string | null> = {
      tab: this.activeTabIndex > 0 ? String(this.activeTabIndex) : null,
      building: this.selectedBuilding ?? null,
      search: this.searchControl.value?.trim() ? this.searchControl.value.trim() : null
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  onTabChange(index: number): void {
    this.activeTabIndex = index;
    this.updateUrlParams();
  }

  /**
   * Handle building selection from the filter component
   */
  onBuildingSelected(building: string | null): void {
    this.selectedBuilding = building;
    this.updateUrlParams();
    this.applyBuildingFilter();
  }

  /**
   * Handle display mode change from dropdown
   * Just saves the preference - actual display is handled by GuestInfoComponent
   */
  onDisplayModeChange(): void {
    this.dataSourceStorage.setDataSource(this.selectedDisplayMode);
  }

  /**
   * Apply building filter to all data sets
   */
  private applyBuildingFilter(): void {
    this.applyFilters();
  }

  private applyPendingRowUpdate(): void {
    if (!this.pendingRowUpdate) return;
    const { id, email, phone } = this.pendingRowUpdate;

    const arrays: { guest?: GuestInfo; id: number }[][] = [
      this.confirmableBookings as unknown as { guest?: GuestInfo; id: number }[],
      this.arrivals as unknown as { guest?: GuestInfo; id: number }[],
      this.guests as unknown as { guest?: GuestInfo; id: number }[],
      this.departures as unknown as { guest?: GuestInfo; id: number }[]
    ];

    let patched = false;
    for (const arr of arrays) {
      const idx = arr.findIndex((row) => row.id === id);
      if (idx !== -1 && arr[idx].guest) {
        arr[idx] = {
          ...arr[idx],
          guest: {
            ...arr[idx].guest,
            ...(email !== undefined ? { bookingEmail: email } : {}),
            ...(phone !== undefined ? { bookingPhone: phone } : {})
          }
        };
        patched = true;
      }
    }

    if (patched) {
      this.pendingRowUpdate = null;
    }
  }

  /**
   * Apply both building and search filters to all data sets
   */
  private applyFilters(): void {
    let filteredConfirmable = [...this.confirmableBookings];
    let filteredArrivals = [...this.arrivals];
    let filteredGuests = [...this.guests];
    let filteredDepartures = [...this.departures];

    // Apply building filter
    if (this.selectedBuilding !== null) {
      filteredConfirmable = filteredConfirmable.filter((item) => item.flatInfos[0].buildingName === this.selectedBuilding);
      filteredArrivals = filteredArrivals.filter((item) => item.flatInfos[0].buildingName === this.selectedBuilding);
      filteredGuests = filteredGuests.filter((item) => item.flatInfos[0].buildingName === this.selectedBuilding);
      filteredDepartures = filteredDepartures.filter((item) => item.flatInfos[0].buildingName === this.selectedBuilding);
    }

    // Apply search filter
    const searchTerm = this.searchControl.value?.toLowerCase().trim();
    if (searchTerm) {
      filteredConfirmable = filteredConfirmable.filter((item) => this.matchesSearchTerm(item, searchTerm));
      filteredArrivals = filteredArrivals.filter((arrival) => this.matchesSearchTerm(arrival, searchTerm));
      filteredGuests = filteredGuests.filter((item) => this.matchesSearchTerm(item, searchTerm));
      filteredDepartures = filteredDepartures.filter((item) => this.matchesSearchTerm(item, searchTerm));
    }

    // Update filtered arrays
    this.filteredConfirmableBookings = filteredConfirmable;
    this.filteredArrivals = filteredArrivals;
    this.filteredGuests = filteredGuests;
    this.filteredDepartures = filteredDepartures;
  }

  /**
   * Check if any booking row matches the search term
   * @param row The booking row to check (any type)
   * @param searchTerm The search term (already lowercase and trimmed)
   * @returns True if the row matches the search term
   */
  private matchesSearchTerm(row: any, searchTerm: string): boolean {
    // Search in guest information (common for all booking types)
    if (row.guest?.name?.toLowerCase().includes(searchTerm)) {
      return true;
    }
    if (row.guest?.email?.toLowerCase().includes(searchTerm)) {
      return true;
    }
    if (row.guest?.phone?.toLowerCase().includes(searchTerm)) {
      return true;
    }

    // Search in flat information - handle different structures
    if (row.flatInfos) {
      // ArrivalBookingRow
      for (const flatInfo of row.flatInfos) {
        if (flatInfo?.buildingName?.toLowerCase().includes(searchTerm)) {
          return true;
        }
        if (flatInfo?.flatName?.toLowerCase().includes(searchTerm)) {
          return true;
        }
      }
    } else if (row.flatInfo) {
      // ConfirmableBookingRow, GuestBookingRow, DepartureBookingRow
      if (row.flatInfo?.buildingName?.toLowerCase().includes(searchTerm)) {
        return true;
      }
      if (row.flatInfo?.flatName?.toLowerCase().includes(searchTerm)) {
        return true;
      }
    }

    // Search in entry codes (for ArrivalBookingRow)
    if (row.entryCode?.toLowerCase().includes(searchTerm)) {
      return true;
    }
    if (row.entryCodes) {
      for (const code of row.entryCodes) {
        if (code?.toLowerCase().includes(searchTerm)) {
          return true;
        }
      }
    }

    // Search in dates (formatted) - common for all booking types
    if (row.checkInDate?.toString().includes(searchTerm)) {
      return true;
    }
    if (row.checkOutDate?.toString().includes(searchTerm)) {
      return true;
    }

    return false;
  }

  /**
   * Clear the search field and refresh filters
   */
  clearSearch(): void {
    this.searchControl.setValue('');
  }

  onLookaheadChange(): void {
    const min = this.config.CONFIRMABLE_LOOKAHEAD_MIN_DAYS;
    const max = this.config.CONFIRMABLE_LOOKAHEAD_MAX_DAYS;
    if (this.confirmableLookaheadDays < min) this.confirmableLookaheadDays = min;
    if (this.confirmableLookaheadDays > max) this.confirmableLookaheadDays = max;
    this.lookaheadStorage.setDays(this.confirmableLookaheadDays);
    this.loadConfirmableBookings();
  }

  /**
   * Load confirmable bookings data
   */
  protected loadConfirmableBookings(): void {
    this.confirmableLoad?.unsubscribe();
    this.setListStatus('confirmable', 'loading');
    this.confirmableLoad = this.bookingService
      .getConfirmableBookings(this.confirmableLookaheadDays)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: ConfirmableBookingRow[]) => {
          this.confirmableBookings = data;
          this.filteredConfirmableBookings = [...data];
          this.applyPendingRowUpdate();
          this.applyFilters();
          this.setListStatus('confirmable', 'ready');
          this.log.debug('Confirmable bookings loaded', this.confirmableBookings.length);
          // Load presence for all confirmable rows if there are any
          if (this.confirmableBookings.length > 0) {
            this.applyPresenceTo(this.confirmableBookings);
          }
        },
        error: (error: unknown) => {
          this.log.error('Failed to load confirmable bookings', error);
          this.setListStatus('confirmable', 'error');
        }
      });
  }

  /**
   * Load arrivals data
   */
  protected loadArrivals(): void {
    this.setListStatus('arrivals', 'loading');
    this.bookingService
      .getArrivals()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: ArrivalBookingRow[]) => {
          this.arrivals = data;
          this.filteredArrivals = [...data];
          this.applyPendingRowUpdate();
          this.applyFilters();
          this.setListStatus('arrivals', 'ready');
          this.log.debug('Arrivals loaded', this.arrivals.length);
          // Load entry codes for all arrival rows if there are any
          if (this.arrivals.length > 0) {
            this.applyEntryCodesTo(this.arrivals, 'Arrivals');
            this.applyPresenceWarningsTo(this.arrivals);
            this.applyPresenceTo(this.arrivals);
          }
        },
        error: (error: unknown) => {
          this.log.error('Failed to load arrivals', error);
          this.setListStatus('arrivals', 'error');
        }
      });
  }

  /**
   * Load guests data
   */
  protected loadGuests(): void {
    this.setListStatus('guests', 'loading');
    this.bookingService
      .getGuests()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: GuestBookingRow[]) => {
          this.guests = data;
          this.filteredGuests = [...data];
          this.applyPendingRowUpdate();
          this.applyFilters();
          this.setListStatus('guests', 'ready');
          this.log.debug('Guests loaded', this.guests);
          // Load entry codes for all guest rows if there are any
          if (this.guests.length > 0) {
            this.applyEntryCodesTo(this.guests, 'Guests');
            this.applyPresenceTo(this.guests);
          }
        },
        error: (error: unknown) => {
          this.log.error('Failed to load guests', error);
          this.setListStatus('guests', 'error');
        }
      });
  }

  /**
   * Load departures data
   */
  protected loadDepartures(): void {
    this.setListStatus('departures', 'loading');
    this.bookingService
      .getDepartures()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: DepartureBookingRow[]) => {
          this.departures = data;
          this.filteredDepartures = [...data];
          this.applyPendingRowUpdate();
          this.applyFilters();
          this.setListStatus('departures', 'ready');
          this.log.debug('Departures loaded', this.departures.length);
          // Load entry codes for all departure rows if there are any
          if (this.departures.length > 0) {
            this.applyEntryCodesTo(this.departures, 'Departures');
            this.applyPresenceTo(this.departures);
          }
        },
        error: (error: unknown) => {
          this.log.error('Failed to load departures', error);
          this.setListStatus('departures', 'error');
        }
      });
  }

  /** A jelenlét- és belépőkód-index lekérése lapbetöltéskor, a listákkal párhuzamosan (spec 6.3). */
  private loadIndexes(): void {
    this.presenceService
      .loadPresenceIndex()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((index) => this.presenceIndex.next(index));
    this.entryCodeService
      .loadEntryCodeIndex()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((index) => this.entryCodeIndex.next(index));
  }

  /** Jelenlét a sorokra az indexből; a sorokat a hívás pillanatában rögzíti. */
  private applyPresenceTo(rows: readonly PresenceRow[]): void {
    rows.forEach((row) => this.presenceService.markPresenceLoading(row));
    this.presenceIndex.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((index) => {
      rows.forEach((row) => this.presenceService.applyPresence(row, index));
      this.applyBuildingFilter();
    });
  }

  /** Érkezőknél: 15:00 utáni jelenlét bejelentkezés nélkül, ugyanabból a jelenlét-indexből. */
  private applyPresenceWarningsTo(rows: readonly ArrivalBookingRow[]): void {
    rows.forEach((row) => this.presenceWarningService.markPresenceWarningLoading(row));
    this.presenceIndex.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((index) => {
      rows.forEach((row) => this.presenceWarningService.applyPresenceWarning(row, index));
      this.applyBuildingFilter();
    });
  }

  /** Belépőkódok a sorokra az indexből; a címke a validációs naplóban jelzi a fület. */
  private applyEntryCodesTo(rows: readonly EntryCodeRow[], label: string): void {
    rows.forEach((row) => this.entryCodeService.markEntryCodesLoading(row));
    this.entryCodeIndex.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((index) => {
      rows.forEach((row) => this.entryCodeService.applyEntryCodes(row, index, label));
      this.applyBuildingFilter();
    });
  }

  /**
   * Get loading state for a specific action
   */
  private isActionLoading(actionKey: string): boolean {
    return this.actionLoadingStates.get(actionKey) === true;
  }

  /**
   * Set loading state for a specific action
   */
  setActionLoading(actionKey: string, isLoading: boolean): void {
    this.actionLoadingStates.set(actionKey, isLoading);
  }

  /**
   * Handle view booking action
   */
  onViewBookingClick(bookingId: number): void {
    this.router.navigate(['/bookings', bookingId], {
      state: { from: 'booking-overview', sourceUrl: this.router.url }
    });
  }

  /**
   * Handle chain-link action from the Departures table
   * Navigates to the booking detail page and requests the chain-link dialog to open
   */
  onChainLinkRequested(bookingId: number): void {
    this.router.navigate(['/bookings', bookingId], {
      state: { from: 'booking-overview', sourceUrl: this.router.url, openChainDialog: true }
    });
  }

  /**
   * Handle communication button click from tables
   */
  onCommunicationClicked(booking: ConfirmableBookingRow | ArrivalBookingRow | GuestBookingRow | DepartureBookingRow): void {
    this.selectedGuestForCommunication.set(booking.guest);

    // Store entry codes from the booking row (if available)
    // DepartureBookingRow doesn't have entryCodes, so we check if it exists
    const entryCodes = 'entryCodes' in booking ? booking.entryCodes || [] : [];
    this.selectedEntryCodesForCommunication.set(entryCodes);

    // Fetch the full booking data with reservation code
    this.bookingClientService.getItem(booking.id).subscribe({
      next: (fullBooking: IBookingModel) => {
        this.selectedBookingForCommunication.set(fullBooking);
        this.showCommunicationModal.set(true);
      },
      error: (error) => {
        this.log.error('Failed to load booking data', error);
        // Fallback to minimal booking model
        const fallbackBooking = this.createFallbackBooking(booking);
        this.selectedBookingForCommunication.set(fallbackBooking);
        this.showCommunicationModal.set(true);
      }
    });
  }

  /**
   * An overview row's flats as the host activity log stores them
   * @param row The row, if found
   * @returns The event flats (the "no flat" placeholder drops out when logged)
   */
  private activityFlatsOf(row: { readonly flatInfos: readonly FlatInfo[] } | undefined): (IActivityFlat | null)[] {
    return (row?.flatInfos ?? []).map(activityFlatOfInfo);
  }

  /**
   * Create fallback booking model when API call fails
   */
  private createFallbackBooking(bookingRow: ConfirmableBookingRow | ArrivalBookingRow | GuestBookingRow | DepartureBookingRow): IBookingModel {
    return {
      id: bookingRow.id,
      reservation_code: undefined, // This will cause the link generation to use placeholder
      name: bookingRow.guest.name,
      email_address: bookingRow.guest.email,
      phone_number: bookingRow.guest.phone,
      start_date: 'checkInDate' in bookingRow ? bookingRow.checkInDate : undefined,
      end_date: 'checkOutDate' in bookingRow ? bookingRow.checkOutDate : undefined
    } as IBookingModel;
  }

  /**
   * Handle communication modal closed
   */
  onCommunicationModalClosed(): void {
    this.showCommunicationModal.set(false);
    this.selectedGuestForCommunication.set(null);
    this.selectedBookingForCommunication.set(null);
    this.selectedEntryCodesForCommunication.set([]);
  }

  onCopyCheckinLinkClicked(booking: ArrivalBookingRow): void {
    if (!booking.reservationCode) return;
    const baseUrl = this.apiService.getBaseUrl();
    const domain = baseUrl.includes('teszt') ? 'teszt.i11.hu' : 'i11.hu';
    const link = `https://${domain}/cin/${booking.reservationCode}`;
    navigator.clipboard.writeText(link).then(() => {
      this.hostActivityLog.log('checkin_link_copy', bookingRefMetadata({ bookingId: booking.id, flats: this.activityFlatsOf(booking) }));
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('common.success'),
        detail: this.translateService.instant('bookingOverview.checkinLinkCopied'),
        life: 3000
      });
    });
  }

  onSendCheckinSmsClicked(booking: ArrivalBookingRow): void {
    if (!booking.reservationCode) return;
    this.selectedBookingForCheckinSms.set(booking);
    this.showCheckinSmsModal.set(true);
  }

  onCheckinSmsModalClosed(): void {
    this.showCheckinSmsModal.set(false);
    this.selectedBookingForCheckinSms.set(null);
  }

  /**
   * Handle generate invoice action
   */
  onGenerateInvoiceClick(bookingId: number): void {
    const actionKey = `invoice_${bookingId}`;
    if (this.isActionLoading(actionKey)) return;

    this.setActionLoading(actionKey, true);

    this.bookingActionService.generateInvoice(bookingId).subscribe({
      next: () => {
        const flats = this.activityFlatsOf(this.guests.find((b) => b.id === bookingId));
        this.hostActivityLog.log('invoice_generate', bookingRefMetadata({ bookingId, flats }));
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.success'),
          detail: this.translateService.instant('bookingOverview.invoiceGenerated')
        });
        this.setActionLoading(actionKey, false);
      },
      error: (err) => {
        this.log.error('Failed to generate invoice', err);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: this.translateService.instant('bookingOverview.invoiceGenerationFailed')
        });
        this.setActionLoading(actionKey, false);
      }
    });
  }

  /**
   * Handle check out action
   */
  onCheckOutClick(bookingId: number): void {
    const actionKey = `checkout_${bookingId}`;
    if (this.isActionLoading(actionKey)) return;

    this.setActionLoading(actionKey, true);

    this.bookingActionService.checkOut(bookingId).subscribe({
      next: () => {
        const booking = this.departures.find((b) => b.id === bookingId);
        this.hostActivityLog.log('checkout', bookingRefMetadata({ bookingId, flats: this.activityFlatsOf(booking) }));
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.success'),
          detail: this.translateService.instant('bookingOverview.checkedOut')
        });
        // Update the booking in the list
        if (booking) {
          booking.checkOutDateTime = new Date().toISOString();
          this.applyBuildingFilter(); // Refresh filtered lists
        }
        this.setActionLoading(actionKey, false);
      },
      error: (err) => {
        this.log.error('Failed to check out guest', err);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: this.translateService.instant('bookingOverview.checkOutFailed')
        });
        this.setActionLoading(actionKey, false);
      }
    });
  }

  /**
   * Handle cancel check out action
   */
  onCancelCheckOutClick(bookingId: number): void {
    const actionKey = `cancel_checkout_${bookingId}`;
    if (this.isActionLoading(actionKey)) return;

    this.setActionLoading(actionKey, true);

    this.bookingActionService.cancelCheckOut(bookingId).subscribe({
      next: () => {
        const booking = this.departures.find((b) => b.id === bookingId);
        this.hostActivityLog.log('checkout_cancel', bookingRefMetadata({ bookingId, flats: this.activityFlatsOf(booking) }));
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.success'),
          detail: this.translateService.instant('bookingOverview.checkOutCancelled')
        });
        // Update the booking in the list
        if (booking) {
          booking.checkOutDateTime = undefined;
          this.applyBuildingFilter(); // Refresh filtered lists
        }
        this.setActionLoading(actionKey, false);
      },
      error: (err) => {
        this.log.error('Failed to cancel check out', err);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: this.translateService.instant('bookingOverview.checkOutCancellationFailed')
        });
        this.setActionLoading(actionKey, false);
      }
    });
  }

  /**
   * Handle copy checkin action
   * Copies checkin to clipboard with success toast
   */
  onCopyCheckin(booking: ArrivalBookingRow | GuestBookingRow | DepartureBookingRow): void {
    if (!booking.id) {
      this.log.warn('Cannot copy checkin: booking ID is missing');
      return;
    }
    this.bookingClientService.getItem(booking.id).subscribe({
      next: (fullBooking) => {
        if (!fullBooking.checkin) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translateService.instant('common.warning'),
            detail: this.translateService.instant('bookingOverview.copyCheckin.noCheckin')
          });
          return;
        }
        const checkinIri = typeof fullBooking.checkin === 'string' 
          ? fullBooking.checkin 
          : fullBooking.checkin['@id'];
        if (!checkinIri) {
          this.log.error('Checkin IRI is missing', fullBooking.checkin);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.error'),
            detail: this.translateService.instant('bookingOverview.copyCheckin.error')
          });
          return;
        }
        this.clipboardService.copy(checkinIri, {
          id: booking.id,
          reservationCode: 'reservationCode' in booking ? (booking as any).reservationCode : `#${booking.id}`,
          guestName: booking.guest.name,
          startDate: 'checkInDate' in booking ? booking.checkInDate : undefined,
          endDate: 'checkOutDate' in booking ? booking.checkOutDate : undefined
        });
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.success'),
          detail: this.translateService.instant('bookingOverview.copyCheckin.success')
        });
      },
      error: (err) => {
        this.log.error('Failed to load booking for copy checkin', err);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: this.translateService.instant('bookingOverview.copyCheckin.error')
        });
      }
    });
  }

  /**
   * Handle paste checkin action
   * Shows confirmation modal with preview
   */
  onPasteCheckin(booking: ArrivalBookingRow | GuestBookingRow | DepartureBookingRow): void {
    const clipboard = this.clipboardService.get();
    if (!clipboard || !booking.id) {
      return;
    }
    this.selectedTargetBooking.set(booking);
    this.showPasteCheckinModal.set(true);
  }

  /**
   * Handle paste checkin modal closed
   */
  onPasteCheckinModalClosed(): void {
    this.showPasteCheckinModal.set(false);
    this.selectedTargetBooking.set(null);
    this.pasteCheckinLoading.set(false);
  }

  /**
   * Confirm and execute paste checkin operation
   */
  confirmPasteCheckin(): void {
    const clipboard = this.clipboardService.get();
    const targetBooking = this.selectedTargetBooking();
    if (!clipboard || !targetBooking) {
      return;
    }
    this.pasteCheckinLoading.set(true);
    this.bookingActionService.assignCheckinToBooking(targetBooking.id, clipboard.checkinIri).subscribe({
      next: (updatedBooking) => {
        const pasted = { bookingId: targetBooking.id, flats: this.activityFlatsOf(targetBooking), sourceBookingId: clipboard.sourceBooking.id };
        this.hostActivityLog.log('checkin_paste', checkinPasteMetadata(pasted));
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.success'),
          detail: this.translateService.instant('bookingOverview.pasteCheckin.success')
        });
        const arrivalIndex = this.arrivals.findIndex((b) => b.id === targetBooking.id);
        if (arrivalIndex !== -1) {
          this.arrivals[arrivalIndex].isCheckedIn = true;
        }
        const guestIndex = this.guests.findIndex((b) => b.id === targetBooking.id);
        if (guestIndex !== -1) {
          this.guests[guestIndex].isCheckedIn = true;
        }
        const departureIndex = this.departures.findIndex((b) => b.id === targetBooking.id);
        if (departureIndex !== -1) {
          this.departures[departureIndex].isCheckedIn = true;
        }
        this.applyBuildingFilter();
        this.clipboardService.clear();
        this.onPasteCheckinModalClosed();
      },
      error: (err) => {
        this.log.error('Failed to paste checkin', err);
        const errorMessage = err.message === 'ALREADY_CHECKED_IN'
          ? this.translateService.instant('bookingOverview.pasteCheckin.alreadyCheckedIn')
          : this.translateService.instant('bookingOverview.pasteCheckin.error');
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: errorMessage
        });
        this.pasteCheckinLoading.set(false);
      }
    });
  }

  /**
   * Check if booking has period data
   */
  hasBookingPeriod(booking: ArrivalBookingRow | GuestBookingRow | DepartureBookingRow): boolean {
    return 'checkInDate' in booking && 'checkOutDate' in booking;
  }

  /**
   * Helper method to get booking period string
   */
  getBookingPeriod(booking: ArrivalBookingRow | GuestBookingRow | DepartureBookingRow): string {
    if ('checkInDate' in booking && 'checkOutDate' in booking) {
      return `${booking.checkInDate} → ${booking.checkOutDate}`;
    }
    return '';
  }

  /**
   * Handle presence click from any table - open presence detail modal
   */
  onPresenceClick(event: any): void {
    this.log.debug('Presence clicked', event);
    if (event && event.booking && event.presenceStatus) {
      this.selectedPresenceBooking.set(event);
      this.presenceModalVisible.set(true);
    }
  }

  /**
   * Close presence detail modal
   */
  onPresenceModalClose(): void {
    this.presenceModalVisible.set(false);
    this.selectedPresenceBooking.set(null);
  }

  /**
   * Get formatted time since presence change
   */
  getPresenceTimeSince(minutes: number): string {
    if (minutes < 1) return this.translateService.instant('presence.lessThan1MinuteAgo');
    if (minutes < 60) return this.translateService.instant('presence.minutesAgo', { count: Math.floor(minutes) });
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return this.translateService.instant('presence.1HourAgo');
    if (hours < 24) return this.translateService.instant('presence.hoursAgo', { count: hours });
    const days = Math.floor(hours / 24);
    return this.translateService.instant('presence.daysAgo', { count: days });
  }

  /**
   * Format date time for display in modal
   */
  formatPresenceDateTime(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('hu-HU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  }
}
